import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { RadioButton } from 'primereact/radiobutton';
import { useMemo } from 'react';
import { CatalogProject } from '../../../Helper/ApiHelper/LvEpNetworkHelper';
import { formatDate } from '../../../Helper/Util/DateHelper';
import { EMPTY_FIELD_PLACEHOLDER } from '../../../Helper/Statics/Constants';

const DEFAULT_INTRO =
  'Wählen Sie einen EP-Katalog aus der Katalogbibliothek (Beestate-Plattform).';

/**
 * One table row for the EP catalog picker (built from {@link CatalogProject} API data).
 * {@link EpCatalogPickerRow.id} is the catalog **version** id (same as {@link EpCatalogPickerSelection.versionId}).
 */
export type EpCatalogPickerRow = {
  id: string;
  /** External EP catalog project id (Beestate platform). */
  epCatalogProjectId: string;
  title: string;
  versionTitle: string;
  updatedAt: string;
  description: string;
};

/** Payload for copying an external catalog version onto a phase. */
export type EpCatalogPickerSelection = {
  epCatalogProjectId: string;
  versionId: string;
};

type AlphaEpCatalogPickerStepProps = {
  catalogs: CatalogProject[];
  selected: EpCatalogPickerRow | null;
  onSelectionChange: (row: EpCatalogPickerRow) => void;
  readOnly?: boolean;
  introText?: string;
};

export default function AlphaEpCatalogPickerStep({
  catalogs,
  selected,
  onSelectionChange,
  readOnly = false,
  introText = DEFAULT_INTRO
}: AlphaEpCatalogPickerStepProps) {
  const rows = useMemo(() => pickerRowsFromCatalogs(catalogs), [catalogs]);

  const selectionProps = readOnly
    ? {}
    : {
        selection: selected,
        onSelectionChange: (e: { value: EpCatalogPickerRow }) => {
          const val = e.value;
          if (val) onSelectionChange(val);
        },
        selectionMode: 'single' as const
      };

  function pickerRowsFromCatalogs(
    catalogs: CatalogProject[]
  ): EpCatalogPickerRow[] {
    const rows: EpCatalogPickerRow[] = [];
    for (const project of catalogs) {
      const desc =
        project.description != null && String(project.description).trim() !== ''
          ? String(project.description)
          : EMPTY_FIELD_PLACEHOLDER;
      for (const version of (project.catalogVersions ?? []).filter(
        (v) => !v.deletedAt
      )) {
        rows.push({
          id: String(version.id),
          epCatalogProjectId: String(project.id),
          title: project.title,
          versionTitle: version.versionTitle || EMPTY_FIELD_PLACEHOLDER,
          updatedAt: version.updatedAt,
          description: desc
        });
      }
    }
    rows.sort((a, b) => {
      const byTitle = a.title.localeCompare(b.title, 'de');
      if (byTitle !== 0) return byTitle;
      return (
        new Date(b.updatedAt || 0).getTime() -
        new Date(a.updatedAt || 0).getTime()
      );
    });
    return rows;
  }

  return (
    <div>
      <p className={'text-sm text-color-secondary mb-3'}>{introText}</p>

      <DataTable
        value={rows}
        dataKey={'id'}
        className=""
        emptyMessage={'Keine Katalogversionen verfügbar'}
        {...selectionProps}
      >
        {!readOnly && (
          <Column
            header={''}
            style={{ width: '3.5rem', textAlign: 'center' }}
            headerStyle={{ width: '3.5rem' }}
            body={(row: EpCatalogPickerRow) => (
              <div className={'flex justify-content-center align-items-center'}>
                <RadioButton
                  inputId={'alpha-ep-cat-radio-' + row.id}
                  name={'alpha-ep-catalog-picker'}
                  value={row}
                  checked={!!selected && selected.id === row.id}
                  disabled={readOnly}
                  onChange={(e) => {
                    if (readOnly) return;
                    onSelectionChange(e.value as EpCatalogPickerRow);
                  }}
                />
              </div>
            )}
          />
        )}
        <Column
          header={'Katalog'}
          body={(row: EpCatalogPickerRow) => row.title}
        />
        <Column
          header={'Version'}
          body={(row: EpCatalogPickerRow) => row.versionTitle}
        />
        <Column
          header={'Zuletzt aktualisiert'}
          body={(row: EpCatalogPickerRow) => formatDate(row.updatedAt)}
          style={{ width: '11rem' }}
        />
        <Column
          header={'Beschreibung'}
          body={(row: EpCatalogPickerRow) => (
            <span className={'white-space-normal'}>{row.description}</span>
          )}
        />
      </DataTable>
    </div>
  );
}
